import dayjs from "dayjs";
import { useState } from "react";
import { useIntl } from "react-intl";
import { uploadToPresignedUrl } from "../../../common/common.image.upload";
import { usePublicUser } from "../../../context/PublicUserContext";
import { DimensionOption } from "../../../types";
import { Dimensions } from "../../Dimensions";
import { SeparatorLine } from "../../SeparatorLine";

type StepSelectCategoryProps = {
	setStep: (step: number) => void;
	register: any;
	errors: any;
	watch: any;
	setValue: any;
	handleSubmit: any;
	categoriesList: any[];
	subCategories: any[];
	dimensionsOptions: { [key: number]: DimensionOption };
	getValues: any;
};

export function StepSelectCategory({
	setStep,
	register,
	errors,
	watch,
	setValue,
	handleSubmit,
	categoriesList,
	subCategories,
	dimensionsOptions,
	getValues,
}: StepSelectCategoryProps) {
	const intl = useIntl();

	const [uploadingAdditionalImage, setUploadingAdditionalImage] = useState(false);
	const [imagesList, setImagesList] = useState<Blob[]>([]);

	const { portalUid } = usePublicUser();

	const mainCategory = watch("lossObjectMainCategory") || null;
	const subCategory = watch("lossObjectSubCategory") || null;

	function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
		if (!e.target.files) return;
		const files = Array.from(e.target.files);
		setImagesList((prev) => [...prev, ...files.map((file) => file as Blob)].slice(0, 3));
		e.target.value = "";
	}

	function removeImage(idx: number) {
		setImagesList((prev) => prev.filter((_, i) => i !== idx));
	}

	async function handleContinue() {
		if (imagesList.length > 0) {
			const images: string[] = [];
			setUploadingAdditionalImage(true);
			for (const file of imagesList) {
				const imageUrl = await uploadToPresignedUrl(file as File, portalUid || "", true);
				images.push(imageUrl);
			}
			setValue("lossObjectImages", images);
		}

		handleSubmit();
		setStep(2);
	}

	const subcategoriesList = mainCategory ? subCategories.filter((a: any) => a.parentCategoryId == mainCategory) : [];

	return (
		<div className="">
			<SeparatorLine />
			<h5 className="mb-2 mt-2 fw-bold" style={{ color: "#bc231e" }}>
				{intl.formatMessage({ id: "loss_report_categories_step1" })}
			</h5>

			<div className="row mt-3">
				<div className="col-sm-12">
					<div className="form-group">
						<div className="input-group mb-3">
							<label
								htmlFor="salutation"
								className="col-12 col-md-3 col-lg-3 justify-content-start align-items-center d-flex form-label fw-bold"
							>
								{intl.formatMessage({ id: "loss_report_main_category" })}
							</label>
							<select
								className={`form-select${errors.lossObjectMainCategory ? " is-invalid" : ""} rounded`}
								{...register("lossObjectMainCategory")}
							>
								<option value="">Select main category</option>
								{categoriesList.map((opt) => (
									<option key={opt.categoryId} value={opt.categoryId}>
										{opt.localizedValue}
									</option>
								))}
							</select>
							{errors.lossObjectMainCategory && (
								<div className="invalid-feedback d-block">
									{errors.lossObjectMainCategory.message as string}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			<div className="row">
				<div className="col-sm-12">
					<div className="form-group">
						<div className="input-group mb-3">
							<label
								htmlFor="salutation"
								className="col-12 col-md-3 col-lg-3 justify-content-start align-items-center d-flex form-label fw-bold"
							>
								{intl.formatMessage({ id: "loss_report_sub_category" })}
							</label>
							<select
								className={`form-select${errors.lossObjectSubCategory ? " is-invalid" : ""} rounded`}
								{...register("lossObjectSubCategory")}
								disabled={!watch("lossObjectMainCategory")}
							>
								<option value="">Select sub-category</option>
								{subcategoriesList.map((opt) => (
									<option key={opt.categoryId} value={opt.categoryId}>
										{opt.localizedValue}
									</option>
								))}
							</select>
							{errors.lossObjectSubCategory && (
								<div className="invalid-feedback d-block">
									{errors.lossObjectSubCategory.message as string}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{subCategory && (
				<>
					<div className="row">
						<div className="col-sm-12">
							<div className="form-group">
								<div className="input-group mb-3">
									<label
										htmlFor="salutation"
										className="col-12 col-md-3 col-lg-3 justify-content-start align-items-center d-flex form-label fw-bold"
									></label>
									<div className="col-12 col-md-9 col-lg-9 p-0">
										<Dimensions
											// @ts-expect-error
											dimensionsAndOptions={Object.values(dimensionsOptions)}
											register={register}
											getValues={getValues}
											errors={errors}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</>
			)}

			<SeparatorLine />

			<h5 className="mb-2 mt-2 fw-bold" style={{ color: "#bc231e" }}>
				{intl.formatMessage({ id: "loss_report_categories_step2" })}
			</h5>

			<p className="" style={{}}>
				{intl.formatMessage({ id: "loss_report_categories_step2_subtitle" })}
			</p>

			<div className="row">
				<div className="col-sm-12">
					<div className="form-group">
						<div className="input-group mb-3">
							<label
								htmlFor="lossObjectLostDate"
								className="col-12 col-md-3 col-lg-3 justify-content-start align-items-center d-flex form-label fw-bold"
							>
								{intl.formatMessage({ id: "loss_report_last_seen_date" })}
							</label>
							<input
								type="date"
								className="form-control rounded"
								{...register("lossObjectLostDate")}
								min={dayjs().add(-3, "month").format("YYYY-MM-DD")}
								max={dayjs().format("YYYY-MM-DD")}
							/>
						</div>
					</div>
				</div>
			</div>

			<SeparatorLine />

			<h5 className="mb-2 mt-2 fw-bold" style={{ color: "#bc231e" }}>
				{intl.formatMessage({ id: "loss_report_categories_step3" })}
			</h5>

			<div className="row mt-3">
				<div className="col-sm-12">
					<div className="form-group">
						<div className="input-group mb-3">
							<label
								htmlFor="salutation"
								className="col-12 col-md-3 col-lg-3 justify-content-start align-items-center d-flex form-label fw-bold"
							>
								{intl.formatMessage({ id: "loss_report_upload_image" })}
							</label>
							<input
								type="file"
								className="form-control rounded"
								accept="image/*"
								multiple
								onChange={handleImageChange}
								disabled={imagesList.length >= 3}
							/>
						</div>
					</div>
					<div className="d-flex gap-1 mt-1">
						{imagesList &&
							Array.from(imagesList).map((img, idx) => (
								<div key={idx} className="position-relative">
									<img
										src={URL.createObjectURL(img)}
										alt={`upload-${idx}`}
										style={{
											width: 60,
											height: 60,
											objectFit: "cover",
											borderRadius: 6,
											border: "1px solid #ccc",
										}}
									/>
									<button
										type="button"
										className="btn-close position-absolute top-0 end-0 bg-white"
										style={{ fontSize: 8 }}
										onClick={() => removeImage(idx)}
										aria-label="Remove"
									/>
								</div>
							))}
					</div>
				</div>
			</div>

			<div className="d-grid gap-2 d-md-flex justify-content-md-end mt-3">
				<button
					className="btn btn-primary"
					type="button"
					onClick={handleContinue}
					disabled={!mainCategory || !subCategory || uploadingAdditionalImage}
				>
					{intl.formatMessage({ id: "loss_report_continue" })}
				</button>
			</div>
		</div>
	);
}
